/**
 * O painel do eixo Identidade, com o vínculo da peça (E84.F3).
 *
 * Módulo próprio porque `QuadroPorEixosPaineis` já passa do teto de 200 linhas
 * do handbook §8 — e porque o vínculo importa `FatosDoEixo` de lá: deixar o
 * painel lá dentro fecharia um ciclo de import. Mesma regra dos demais
 * painéis: a frase do que o eixo confronta, os itens que o backend devolveu, e
 * a ficha lida do texto da peça — sem alegação nova. Três estados, os três
 * declarados: exame anterior ao estágio, peça sem camada de texto, texto lido.
 */
import { MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { EncadeamentoApuradoData } from '@/lib/exame_eletronico';

import { ComoLer, FatosDoEixo, type PainelDoEixoProps } from './QuadroPorEixosPaineis';
import { Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;
const tv = ptBR.exameEletronico.quadro.vinculo;

function LinhaDaFicha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-sc-2 text-body-sm">
      <dt className="text-fg-muted">{rotulo}:</dt>
      <dd className="break-all text-fg">
        <MonoData>{valor}</MonoData>
      </dd>
    </div>
  );
}

function FichaDoVinculo({ vinculo }: { vinculo: EncadeamentoApuradoData }): JSX.Element {
  return (
    <section data-testid="quadro-vinculo-ficha">
      <dl className="space-y-sc-1">
        <LinhaDaFicha
          rotulo={tv.codigoDaTransacao}
          valor={vinculo.codigo_da_transacao || tv.naoInformado}
        />
        {vinculo.texto_legivel ? (
          <LinhaDaFicha
            rotulo={tv.codigosLidos}
            valor={
              vinculo.codigos_embutidos.length > 0
                ? vinculo.codigos_embutidos.join(', ')
                : tv.nenhum
            }
          />
        ) : null}
      </dl>
      {vinculo.texto_legivel ? (
        <p className="mt-sc-2 max-w-prose text-caption text-fg-dim">{tv.declaratorio}</p>
      ) : (
        <p className="mt-sc-2 text-body-sm text-fg-dim" data-testid="quadro-vinculo-sem-texto">
          {tv.semTexto}
        </p>
      )}
    </section>
  );
}

/** O vínculo da peça com a transação, lido do texto da própria peça. */
export function VinculoDaPeca({ resultado }: PainelDoEixoProps): JSX.Element {
  const vinculo = resultado.encadeamento ?? null;
  return (
    <section className="space-y-sc-2" data-testid="quadro-vinculo-bloco">
      <Titulo>{tv.titulo}</Titulo>
      {vinculo === null ? (
        <p className="text-body-sm text-fg-dim" data-testid="quadro-vinculo-nao-examinado">
          {tv.naoExaminado}
        </p>
      ) : (
        <>
          <FatosDoEixo itens={vinculo.itens} testId="quadro-vinculo" />
          <FichaDoVinculo vinculo={vinculo} />
        </>
      )}
      <CodigosDasPaginas resultado={resultado} />
    </section>
  );
}

/**
 * E157.F2 (ADR-0107 §2.6): os códigos bidimensionais lidos das páginas renderizadas e os dois
 * estados explícitos (consulta ao portal não executada, barras não lidas). Exame anterior à
 * régua v48 não tem o estágio: o bloco não aparece — nada é preenchido retroativamente.
 */
function CodigosDasPaginas({ resultado }: PainelDoEixoProps): JSX.Element | null {
  const codigos = resultado.codigos_lidos ?? null;
  if (codigos === null) return null;
  return (
    <section className="space-y-sc-1" data-testid="quadro-codigos-lidos-bloco">
      <p className="text-body-sm font-medium text-fg">{tv.codigosDasPaginas}</p>
      <FatosDoEixo itens={codigos.itens} testId="quadro-codigos-lidos" />
    </section>
  );
}

export function PainelIdentidade({ resultado }: PainelDoEixoProps): JSX.Element {
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-identidade">
      <ComoLer texto={t.confronta.identidade} testId="como-ler-identidade" />
      <FatosDoEixo itens={resultado.identidade} testId="quadro-identidade" />
      <VinculoDaPeca resultado={resultado} />
    </div>
  );
}
